/**
 * The two time pickers, both captured in full.
 *
 * `RecurrencePicker` is `21-scheduler-picker.png` → `23-scheduler-weekly-configured.png`:
 * four tabs, the weekday chips `Mo Tu We Th Fr Sa Su`, "Every [N] week, at [time]" and a
 * rendered preview line. `DateTriggerPicker` is `31-datetrigger-picker.png` →
 * `34-datetrigger-configured.png`: two radios and the searchable 15-minute time dropdown.
 */
import { useState } from 'react';
import { RECURRENCE_TYPES, type SchedulerConfig } from '@elizmonday/shared';
import { PopoverCaption } from './SlotPopover';
import { formatTime, localTimezone, parseTimeValue, timeOptions, toLocalTime, toUtcHour } from './time';
import type { SlotEditorProps } from './pickers';

/** Mo…Su, ISO numbering with **Sunday = 7** (sandbox E2). */
const WEEKDAYS: ReadonlyArray<{ iso: number; short: string; long: string }> = [
  { iso: 1, short: 'Mo', long: 'Monday' },
  { iso: 2, short: 'Tu', long: 'Tuesday' },
  { iso: 3, short: 'We', long: 'Wednesday' },
  { iso: 4, short: 'Th', long: 'Thursday' },
  { iso: 5, short: 'Fr', long: 'Friday' },
  { iso: 6, short: 'Sa', long: 'Saturday' },
  { iso: 7, short: 'Su', long: 'Sunday' },
];

const UNIT_OF: Readonly<Record<string, string>> = { Daily: 'day', Weekly: 'week', Monthly: 'month', Yearly: 'year' };

export function TimeSelect({
  value,
  onChange,
  testId,
}: {
  value: string;
  onChange(next: string): void;
  testId: string;
}) {
  return (
    <select
      className="ez-automation-input ez-automation-input--time"
      value={value}
      aria-label="Time"
      data-testid={testId}
      onChange={(event) => onChange(event.target.value)}
    >
      {timeOptions().map((option) => (
        <option key={option.value} value={option.value}>
          {option.text}
        </option>
      ))}
    </select>
  );
}

/** The line under the form: "Every 2 weeks on Sunday and Friday at 7:15 AM". */
export function recurrenceSummary(config: SchedulerConfig, timezone: string): string {
  const unit = UNIT_OF[config.type] ?? 'week';
  const every = config.occurrences > 1 ? `Every ${String(config.occurrences)} ${unit}s` : `Every ${unit}`;
  const time = formatTime(toLocalTime(config.hour, 0, timezone));
  if (config.type === 'Weekly') {
    const names = config.days
      .map((day) => WEEKDAYS.find((entry) => entry.iso === day)?.long)
      .filter((name): name is string => name !== undefined);
    const on = names.length === 0 ? '-' : names.length === 1 ? names[0]! : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]!}`;
    return `${every} on ${on} at ${time}`;
  }
  if (config.type === 'Monthly' || config.type === 'Yearly') {
    const days = config.days.length === 0 ? '-' : config.days.join(', ');
    return `${every} on day ${days} at ${time}`;
  }
  return `${every} at ${time}`;
}

export function RecurrencePicker({ value, onChange }: SlotEditorProps) {
  const timezone = localTimezone();
  const config: SchedulerConfig = {
    type: 'Weekly',
    occurrences: 1,
    hour: 0,
    days: [],
    months: [],
    timezone,
    ...((value ?? {}) as Partial<SchedulerConfig>),
  } as SchedulerConfig;
  const local = toLocalTime(config.hour, 0, config.timezone || timezone);
  const patch = (next: Partial<SchedulerConfig>): void => onChange({ ...config, ...next });

  return (
    <div className="ez-automation-form ez-automation-recurrence" data-testid="recurrence-picker">
      <div className="ez-automation-tabs" role="tablist" aria-label="Recurrence">
        {RECURRENCE_TYPES.map((type) => (
          <button
            key={type}
            type="button"
            role="tab"
            aria-selected={config.type === type}
            className="ez-automation-tabs__tab"
            data-testid={`recurrence-${type.toLowerCase()}`}
            onClick={() => patch({ type, days: [] })}
          >
            {type}
          </button>
        ))}
      </div>

      {config.type === 'Weekly' ? (
        <div className="ez-automation-weekdays" role="group" aria-label="Days of the week">
          {WEEKDAYS.map((day) => (
            <button
              key={day.iso}
              type="button"
              className="ez-automation-weekday"
              aria-pressed={config.days.includes(day.iso)}
              aria-label={day.long}
              data-testid={`recurrence-day-${String(day.iso)}`}
              onClick={() =>
                patch({
                  days: config.days.includes(day.iso)
                    ? config.days.filter((entry) => entry !== day.iso)
                    : [...config.days, day.iso].sort((a, b) => a - b),
                })
              }
            >
              {day.short}
            </button>
          ))}
        </div>
      ) : null}

      {config.type === 'Monthly' || config.type === 'Yearly' ? (
        <div className="ez-automation-form__row">
          <span>On day</span>
          <input
            type="number"
            min={1}
            max={31}
            className="ez-automation-input ez-automation-input--count"
            aria-label="Day of the month"
            data-testid="recurrence-monthday"
            value={config.days[0] ?? 1}
            onChange={(event) => patch({ days: [Math.min(31, Math.max(1, Number(event.target.value) || 1))] })}
          />
        </div>
      ) : null}

      <div className="ez-automation-form__row">
        <span>Every</span>
        <input
          type="number"
          min={1}
          className="ez-automation-input ez-automation-input--count"
          aria-label="Interval"
          data-testid="recurrence-occurrences"
          value={config.occurrences}
          onChange={(event) => patch({ occurrences: Math.max(1, Number(event.target.value) || 1) })}
        />
        <span>{`${UNIT_OF[config.type] ?? 'week'}, at`}</span>
        <TimeSelect
          testId="recurrence-time"
          value={`${String(local.hours)}:${String(local.minutes).padStart(2, '0')}`}
          onChange={(next) => patch({ ...toUtcHour(parseTimeValue(next), config.timezone || timezone), timezone: config.timezone || timezone })}
        />
      </div>

      <div className="ez-automation-preview" data-testid="recurrence-preview">
        {recurrenceSummary(config, config.timezone || timezone)}
      </div>
    </div>
  );
}

/** The `580` trigger's "when to notify on this date?" card. */
export function DateTriggerPicker({ value, onChange }: SlotEditorProps) {
  const timezone = localTimezone();
  const config = {
    hour: 0,
    utcDaysDiff: 0,
    offset: { count: 0, unit: 'days' as const },
    timezone,
    ...((value ?? {}) as Record<string, unknown>),
  } as { hour: number; utcDaysDiff: number; offset: { count: number; unit: string }; timezone: string };
  const [mode, setMode] = useState<'on' | 'offset'>(config.offset.count === 0 ? 'on' : 'offset');
  const local = toLocalTime(config.hour, config.utcDaysDiff, config.timezone || timezone);
  const timeValue = `${String(local.hours)}:${String(local.minutes).padStart(2, '0')}`;
  const patch = (next: Partial<typeof config>): void => onChange({ ...config, ...next });
  const setTime = (next: string): void => patch({ ...toUtcHour(parseTimeValue(next), config.timezone || timezone), timezone: config.timezone || timezone });

  return (
    <div className="ez-automation-form ez-automation-datetrigger" data-testid="datetrigger-picker">
      <PopoverCaption>When to notify on this date?</PopoverCaption>

      <label className="ez-automation-form__row">
        <input
          type="radio"
          name="datetrigger-mode"
          checked={mode === 'on'}
          data-testid="datetrigger-mode-on"
          onChange={() => {
            setMode('on');
            patch({ offset: { count: 0, unit: 'days' } });
          }}
        />
        <span>When date arrives at</span>
        <TimeSelect testId="datetrigger-time-on" value={timeValue} onChange={setTime} />
      </label>

      <label className="ez-automation-form__row">
        <input
          type="radio"
          name="datetrigger-mode"
          checked={mode === 'offset'}
          data-testid="datetrigger-mode-offset"
          onChange={() => {
            setMode('offset');
            patch({ offset: { count: -1, unit: 'days' } });
          }}
        />
        <input
          type="number"
          min={1}
          className="ez-automation-input ez-automation-input--count"
          aria-label="Amount"
          data-testid="datetrigger-count"
          value={Math.abs(config.offset.count) || 1}
          onChange={(event) => {
            const amount = Math.max(1, Number(event.target.value) || 1);
            patch({ offset: { count: config.offset.count < 0 ? -amount : amount, unit: config.offset.unit } });
          }}
        />
        <select
          className="ez-automation-input"
          aria-label="Unit"
          data-testid="datetrigger-unit"
          value={config.offset.unit}
          onChange={(event) => patch({ offset: { count: config.offset.count, unit: event.target.value } })}
        >
          <option value="days">days</option>
          <option value="businessDays">business days</option>
          <option value="weeks">weeks</option>
          <option value="months">months</option>
        </select>
        <select
          className="ez-automation-input"
          aria-label="Before or after"
          data-testid="datetrigger-direction"
          value={config.offset.count < 0 ? 'before' : 'after'}
          onChange={(event) => {
            const amount = Math.abs(config.offset.count) || 1;
            patch({ offset: { count: event.target.value === 'before' ? -amount : amount, unit: config.offset.unit } });
          }}
        >
          <option value="before">before</option>
          <option value="after">after</option>
        </select>
        <span>date arrives, at</span>
        <TimeSelect testId="datetrigger-time-offset" value={timeValue} onChange={setTime} />
      </label>
    </div>
  );
}

/** The sentence label a configured date trigger renders ("1 day before"/"When"). */
export function dateTriggerLabel(config: { offset?: { count?: number; unit?: string } } | null | undefined): string {
  const count = config?.offset?.count ?? 0;
  if (count === 0) return 'When';
  const unit = config?.offset?.unit ?? 'days';
  const amount = Math.abs(count);
  const noun = unit === 'businessDays' ? 'business day' : unit.replace(/s$/, '');
  return `${String(amount)} ${noun}${amount === 1 ? '' : 's'} ${count < 0 ? 'before' : 'after'}`;
}
